'use client';

import { useEffect, useRef } from 'react';
import { MessageSquare, Loader2 } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from './ChatMessage';
import { Loading } from '@/components/ui/loading';
import { EmptyState } from '@/components/ui/empty-state';
import type { ChatMessage as ChatMessageType, ChatJobStatus, StreamingMessageData } from '@/types/chat';

const markdownComponents = {
  table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="table-wrapper">
      <table {...props}>{children}</table>
    </div>
  ),
};

interface ChatMessageListProps {
  messages: ChatMessageType[];
  isLoading?: boolean;
  isTyping?: boolean;
  jobStatus?: ChatJobStatus | null;
  streamingMessage?: StreamingMessageData | null;
}

/**
 * Scrollable container for chat messages.
 *
 * Features:
 * - Auto-scrolls to bottom when new messages arrive
 * - Shows empty state for new conversations
 * - Shows typing indicator while waiting for AI response
 * - Shows research progress for async operations
 */
export function ChatMessageList({
  messages,
  isLoading = false,
  isTyping = false,
  jobStatus = null,
  streamingMessage = null,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change, job status updates, or streaming content grows
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, jobStatus, streamingMessage?.content]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <EmptyState
          icon={<MessageSquare />}
          title="Start a conversation"
          description="Ask questions about software companies. I can help with identifying software companies, understanding their business, and more."
        />
      </div>
    );
  }

  // Determine if we should show progress instead of typing indicator
  const isResearching = jobStatus?.status === 'processing' || jobStatus?.status === 'pending';

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-6">
      <div className="max-w-4xl mx-auto">
        {messages.map(message => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {/* Streaming Message (highest priority) */}
        {streamingMessage ? (
          <StreamingMessageBubble data={streamingMessage} />
        ) : /* Research Progress Indicator */
        isResearching && jobStatus ? (
          <ResearchProgressIndicator jobStatus={jobStatus} />
        ) : (
          /* Typing Indicator for normal operations */
          isTyping && <TypingIndicator />
        )}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

/**
 * Research progress indicator shown during async research operations.
 */
function ResearchProgressIndicator({ jobStatus }: { jobStatus: ChatJobStatus }) {
  // Format stage name for display
  const formatStage = (stage: string): string => {
    return stage
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const progressPercent = jobStatus.progress?.percent ?? 0;
  const stageName = jobStatus.progress?.stage ? formatStage(jobStatus.progress.stage) : 'Processing';

  return (
    <div className="flex gap-3 mb-4">
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-1 text-white flex items-center justify-center">
        <MessageSquare className="w-4 h-4" />
      </div>

      {/* Progress Card */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-2xl rounded-bl-md px-4 py-3 min-w-[280px]">
        <div className="flex items-center gap-2 mb-2">
          <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />
          <span className="text-sm font-medium text-blue-900 dark:text-blue-100">{stageName}</span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-full h-2 overflow-hidden">
          <div
            className="bg-blue-600 dark:bg-blue-500 h-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Progress Percentage */}
        <div className="text-xs text-blue-700 dark:text-blue-300 mt-1 text-right">{progressPercent}%</div>
      </div>
    </div>
  );
}

/**
 * Animated typing indicator shown while waiting for AI response.
 */
function TypingIndicator() {
  return (
    <div className="flex gap-3 mb-4">
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-1 text-white flex items-center justify-center">
        <MessageSquare className="w-4 h-4" />
      </div>

      {/* Dots */}
      <div className="bg-surface-raised rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1">
        <span className="w-2 h-2 bg-text-placeholder rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-text-placeholder rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-text-placeholder rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

/**
 * Streaming message bubble — shows text as it arrives from SSE.
 */
function StreamingMessageBubble({ data }: { data: StreamingMessageData }) {
  const hasContent = data.content.length > 0;

  return (
    <div className="flex gap-3 mb-4">
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-1 text-white flex items-center justify-center">
        <MessageSquare className="w-4 h-4" />
      </div>

      {/* Bubble */}
      <div className="bg-surface-raised rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%] min-w-[120px]">
        {hasContent ? (
          <div className="chat-markdown">
            <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {data.content}
            </Markdown>
            <span className="inline-block w-2 h-4 ml-0.5 bg-blue-500 animate-pulse align-text-bottom" />
          </div>
        ) : (
          /* No text yet — show bouncing dots */
          <div className="flex items-center gap-1">
            <span
              className="w-2 h-2 bg-text-placeholder rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-text-placeholder rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-text-placeholder rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        )}

        {/* Status line */}
        {data.statusMessage && (
          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border-subtle">
            <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />
            <span className="text-xs text-text-secondary">{data.statusMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
}
